<template>
  <div class="search-bar" ref="rootRef">
    <div :class="['input', 'input-row', { hero: hero }]">
      <svg :width="hero ? 16 : 15" :height="hero ? 16 : 15" viewBox="0 0 256 256" fill="currentColor" class="search-icon">
        <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"></path>
      </svg>
      <input
        ref="inputRef"
        class="text-input"
        :value="modelValue"
        :placeholder="placeholder"
        @input="$emit('update:modelValue', $event.target.value)"
        @focus="open = true"
      />
      <LoadingSpinner v-if="loading" />
      <button v-else-if="modelValue" class="clear-btn" aria-label="Clear search" @click="$emit('update:modelValue', '')">×</button>
    </div>

    <div v-show="open && (hasResults || noResults)" class="dropdown elev-md">
      <div v-if="!hasResults" class="empty-state">
        <svg width="28" height="28" viewBox="0 0 256 256" fill="currentColor" class="empty-icon">
          <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"></path>
        </svg>
        <p class="empty-title">No matches for “<span class="empty-query">{{ modelValue.trim() }}</span>”</p>
        <p class="empty-hint text-muted">Check the spelling, or try another movie, show or actor.</p>
      </div>
      <template v-for="group in shownGroups" :key="group.key">
        <div v-if="group.header" class="group-header text-muted">{{ group.header }}</div>
        <div
          v-for="result in group.items"
          :key="group.key + '-' + result.id"
          class="dropdown-row"
          @click="pick(result)"
        >
          <img v-if="result.img" :src="result.img" :class="['thumb', result.shape]" alt="" />
          <span v-else :class="['thumb', 'thumb-blank', result.shape]">{{ result.label.slice(0, 1) }}</span>
          <span class="row-label">{{ result.label }}</span>
          <span :class="['row-sub', 'text-muted', 'sub-' + result.kind]">{{ result.sub }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import LoadingSpinner from './LoadingSpinner.vue';

  export default {
    name: 'SearchBar',
    components: {
      LoadingSpinner
    },
    props: {
      modelValue: {
        type: String,
        required: true
      },
      placeholder: {
        type: String,
        required: true
      },
      groups: {
        type: Array,
        required: true
      },
      loading: {
        type: Boolean,
        default: false
      },
      hero: {
        type: Boolean,
        default: false
      },
      noResults: {
        type: Boolean,
        default: false
      }
    },
    emits: ['update:modelValue', 'select'],
    data() {
      return {
        open: false
      };
    },
    computed: {
      shownGroups() {
        return this.groups.filter(group => group.items.length);
      },
      hasResults() {
        return this.shownGroups.length > 0;
      }
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside);
      // the html autofocus attribute is a one-shot browser pass that client-side
      // rendering can miss; focus explicitly once the input exists
      this.$refs.inputRef.focus();
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
      pick(result) {
        this.open = false;
        this.$emit('select', result);
      },
      handleClickOutside(event) {
        if (!this.$refs.rootRef?.contains(event.target)) {
          this.open = false;
        }
      }
    }
  }
</script>

<style scoped>
  .search-bar {
    position: relative;
  }
  .input-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    color: var(--color-neutral-500);
    font-size: 14px;
    padding: 0 10px;
  }
  .input-row.hero {
    min-height: 48px;
    gap: 9px;
    font-size: 14.5px;
    box-shadow: var(--shadow-md);
  }
  .search-icon {
    flex: none;
  }
  .text-input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: 0;
    font: inherit;
    color: var(--color-text);
    caret-color: var(--color-accent);
    padding: 8px 0;
  }
  .text-input:focus {
    outline: none;
  }
  .text-input::placeholder {
    color: var(--color-neutral-500);
  }
  .clear-btn {
    flex: none;
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--color-neutral-400);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
  }
  .clear-btn:hover {
    background: color-mix(in srgb, var(--color-text) 10%, transparent);
  }
  .dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 20;
    background: var(--color-surface);
    border-radius: var(--radius-md);
    overflow: hidden auto;
    max-height: 380px;
    padding: 6px 0;
    text-align: left;
  }
  .empty-state {
    display: grid;
    justify-items: center;
    gap: 3px;
    padding: 18px 16px 16px;
    text-align: center;
  }
  .empty-icon {
    color: var(--color-neutral-700);
    margin-bottom: 5px;
  }
  .empty-title {
    margin: 0;
    font-size: 13.5px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .empty-query {
    color: var(--color-accent-300);
  }
  .empty-hint {
    margin: 0;
    font-size: 11.5px;
  }
  .group-header {
    font-size: 10.5px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 6px 14px 3px;
  }
  .dropdown-row {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 7px 14px;
    cursor: pointer;
    text-align: left;
  }
  .dropdown-row:hover {
    background: color-mix(in srgb, var(--color-accent) 10%, transparent);
  }
  .thumb {
    width: 30px;
    height: 30px;
    flex: none;
    object-fit: cover;
  }
  .thumb.circle {
    border-radius: 50%;
  }
  .thumb.square {
    border-radius: 6px;
  }
  .thumb-blank {
    display: grid;
    place-items: center;
    background: var(--color-neutral-800);
    color: var(--color-neutral-200);
    font-size: 12px;
    font-weight: 500;
  }
  .row-label {
    font-size: 13.5px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-sub {
    font-size: 11px;
    margin-left: auto;
    /* shrink before the name does, and ellipsise rather than squeeze it */
    flex: 0 1 auto;
    min-width: 0;
    padding-left: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* known-for titles are long; on phones they leave no room for the name */
  @media (max-width: 640px) {
    .sub-actors {
      display: none;
    }
  }
</style>
